<template>
  <DatatableReportV3
    v-if="config"
    title="Opportunities"
    :source="source"
    :config="config"
    :filters="filters"
  />
</template>

<script>
import { mapActions, mapState } from "vuex";
import { customerClassificationColumns } from "@/lib/customerTypes";

import CONSTANTS from "@/Constants.js";
import JobLeadLink from "@/Job/JobLeadLink.vue";
import JobOpportunityLink from "@/Job/Opportunity/JobOpportunityLink.vue";
import MembershipOpportunityLink from "@/Job/Opportunity/MembershipOpportunityLink.vue";
import OpportunityActionsMenu from "@/Job/Opportunity/OpportunityActionsMenu.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import TagList from "@/Tags/TagList.vue";
import store from "@/vuex/store";

export default {
  components: {
    DatatableReportV3
  },
  props: {
    source: {
      type: String,
      default: ""
    },
    filters: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      config: null
    };
  },
  computed: {
    ...mapState(["tenant", "fetchingTenant"])
  },
  watch: {
    tenant(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setConfig();
      }
    }
  },
  mounted() {
    if (!this.tenant && !this.fetchingTenant) {
      this.fetchTenant();
    }
    if (this.tenant) {
      this.setConfig();
    }
    this.$store.dispatch("addBreadcrumb", { label: "Opportunities" });
  },
  methods: {
    ...mapActions(["fetchTenant"]),
    setConfig() {
      this.config = {
        responsive: true,
        order: [[0, "desc"]],
        columns: this.getColumns(),
        vueComponents: [
          {
            rootId: "job-opportunity-modal-vue",
            component: JobOpportunityLink,
            options: {
              store
            }
          },
          {
            rootId: "job-lead-link-vue",
            component: JobLeadLink,
            options: {
              store
            }
          },
          {
            rootId: "opportunity-action-menu-vue",
            component: OpportunityActionsMenu,
            options: { store }
          },
          {
            rootId: "tag-list-vue",
            component: TagList
          },
          {
            rootId: "membership-opportunity-link-vue",
            component: MembershipOpportunityLink,
            options: {
              store
            }
          }
        ]
      };
    },
    getColumns() {
      const { call_rail_enabled } = this.tenant;
      if (call_rail_enabled) {
        return [
          {
            data: "date",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.days,
            filterName: "opportunity_date",
            searchable: false,
            responsivePriority: 3
          },
          {
            data: "job",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.jobs,
            responsivePriority: 2
          },
          {
            data: "tags",
            title: "Job Tags",
            orderable: false,
            searchable: false,
            responsivePriority: 19
          },
          ...customerClassificationColumns(),
          {
            data: "customer",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.customers,
            responsivePriority: 1
          },
          { data: "customer_email", title: "Email", responsivePriority: 17 },
          { data: "customer_phone", title: "Phone", responsivePriority: 6 },
          {
            data: "location_name",
            title: "Location Name",
            responsivePriority: 8
          },
          {
            data: "address",
            orderable: false,
            responsivePriority: 7
          },
          {
            data: "last_appointment_time",
            title: "Last Appointment",
            searchable: false,
            responsivePriority: 18
          },
          {
            data: "department",
            filterName: "department_id",
            searchable: false,
            responsivePriority: 13
          },
          {
            data: "technician",
            title: "Owner",
            searchable: false,
            filterName: "technician_id",
            responsivePriority: 5
          },
          {
            data: "opportunity_status",
            title: "Status",
            orderable: false,
            searchable: false,
            responsivePriority: 4
          },
          {
            data: "lead_type",
            title: "Lead Type",
            searchable: false,
            filterName: "lead_type",
            responsivePriority: 9
          },
          {
            data: "lead_source",
            title: "Lead Source",
            filterName: "lead_source",
            orderable: false,
            searchable: false,
            responsivePriority: 10
          },
          {
            data: "call_claimed_by",
            title: "Call Assigned To",
            filterName: "call_claimed_by",
            searchable: false,
            responsivePriority: 12
          },
          {
            data: "booking_call_source",
            title: "Booking Call Source",
            filterName: "call_source",
            orderable: false,
            searchable: false,
            responsivePriority: 11
          },
          {
            data: "membership_opportunity",
            title:
              "MO? <i class='fa fa-info-circle' data-tippy-content='Membership Opportunity?'></i>",
            className: "text-center",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.membership_opportunity,
            searchable: false,
            footerClassName: "text-center",
            responsivePriority: 15
          },
          {
            data: "membership_sold",
            title:
              "MS? <i class='fa fa-info-circle' data-tippy-content='Membership Sold?'></i>",
            orderable: false,
            className: "text-center",
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.membership_sold,
            searchable: false,
            footerClassName: "text-center",
            responsivePriority: 16
          },
          {
            data: "revenue",
            title: "Revenue",
            className: "text-right",
            searchable: false,
            footerClassName: "text-right",
            responsivePriority: 14
          },
          {
            data: "actions",
            title: "",
            orderable: false,
            searchable: false
          }
        ];
      }
      return [
        {
          data: "date",
          aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.days,
          filterName: "opportunity_date",
          responsivePriority: 3
        },
        {
          data: "job",
          aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.jobs,
          responsivePriority: 2
        },
        {
          data: "tags",
          title: "Job Tags",
          orderable: false,
          responsivePriority: 14
        },
        ...customerClassificationColumns(),
        {
          data: "customer",
          aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.customers,
          responsivePriority: 1
        },
        {
          data: "address",
          orderable: false,
          searchable: true,
          responsivePriority: 6
        },
        {
          data: "last_appointment_time",
          title: "Last Appointment",
          searchable: false,
          responsivePriority: 13
        },
        {
          data: "department",
          filterName: "department_id",
          responsivePriority: 9
        },
        {
          data: "technician",
          title: "Owner",
          filterName: "technician_id",
          responsivePriority: 5
        },
        {
          data: "opportunity_status",
          title: "Status",
          orderable: false,
          responsivePriority: 4
        },
        {
          data: "lead_type",
          title: "Lead Type",
          filterName: "lead_type",
          responsivePriority: 7
        },
        {
          data: "lead_source",
          title: "Lead Source",
          filterName: "lead_source",
          orderable: false,
          responsivePriority: 8
        },
        {
          data: "membership_opportunity",
          title:
            "MO? <i class='fa fa-info-circle' data-tippy-content='Membership Opportunity?'></i>",
          className: "text-center",
          aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.membership_opportunity,
          responsivePriority: 11
        },
        {
          data: "membership_sold",
          title:
            "MS? <i class='fa fa-info-circle' data-tippy-content='Membership Sold?'></i>",
          orderable: false,
          className: "text-center",
          aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.membership_sold,
          searchable: false,
          responsivePriority: 12
        },
        {
          data: "revenue",
          title: "Revenue",
          className: "text-right",
          searchable: false,
          responsivePriority: 10
        },
        {
          data: "actions",
          title: "",
          orderable: false,
          searchable: false
        }
      ];
    }
  }
};
</script>

<style lang="scss" scoped></style>
